import { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { ErrorState } from '@/modules/common/components/organisms/error-state';
import { FullPageLoader } from '@/modules/common/components/organisms/file/full-page-loader';

import { UnsavedChangesModal } from './unsaved-changes-modal';

interface SettingsPageWrapperProps {
  isLoading?: boolean;
  error?: Error | null;
  children: ReactNode;
  onRetry?: () => void;
  unsavedChangesGuard?: {
    isModalOpen: boolean;
    onDiscard: () => void;
    onCancel: () => void;
  };
}

/**
 * Wrapper component for settings pages that handles loading and error states
 * Optionally handles unsaved changes modal via unsavedChangesGuard prop
 *
 * @example
 * ```tsx
 * const unsavedChangesGuard = useUnsavedChangesGuard({
 *   isDirty: form.formState.isDirty,
 *   onDiscard: () => form.reset(),
 * });
 *
 * return (
 *   <SettingsPageWrapper
 *     isLoading={isLoading}
 *     error={error}
 *     unsavedChangesGuard={{
 *       isModalOpen: unsavedChangesGuard.isModalOpen,
 *       onDiscard: unsavedChangesGuard.handleModalDiscard,
 *       onCancel: unsavedChangesGuard.handleModalCancel,
 *     }}
 *   >
 *     {children}
 *   </SettingsPageWrapper>
 * );
 * ```
 */
export function SettingsPageWrapper({
  isLoading,
  error,
  children,
  onRetry,
  unsavedChangesGuard,
}: SettingsPageWrapperProps) {
  const { t } = useTranslation('common');

  if (isLoading) {
    return <FullPageLoader message={t('loading')} fullViewport={false} />;
  }

  if (error) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <ErrorState
          error={error}
          className=""
          action={
            onRetry
              ? {
                  label: t('retry'),
                  onClick: onRetry,
                  variant: 'default',
                }
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <>
      {children}
      {unsavedChangesGuard && (
        <UnsavedChangesModal
          open={unsavedChangesGuard.isModalOpen}
          onClose={unsavedChangesGuard.onCancel}
          onDiscard={unsavedChangesGuard.onDiscard}
        />
      )}
    </>
  );
}
